<script setup>
import { watch, computed } from 'vue'
import { useDisclosure, useFocusTrap, useClickOutside, useKeyPress } from '@cnd_fe/composables'
import { uid } from '@/utils/uid'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  initialFocus: {
    type: HTMLElement,
    default: null
  },
  keyHandlers: {
    type: Object,
    default: () => {}
  },
  hasOwnBackdrop: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const { open, onOpen, onClose: onDisclosureClose } = useDisclosure(props.modelValue)
const { container } = useFocusTrap({ initialFocus: props.initialFocus })

const dialogId = `dialog-${uid('dialog')}`
const titleId = `dialog-title-${uid('dialog')}`
const descriptionId = `dialog-description-${uid('dialog')}`

const onClose = () => {
  onDisclosureClose()
  emit('update:modelValue', false)
}

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      onOpen()
    } else {
      onClose()
    }
  }
)

useClickOutside(() => container.value, onClose)

const handleKeyPress = useKeyPress({
  onEscape: onClose,
  ...props.keyHandlers
})

const dialogProps = computed(() => ({
  ref: container,
  id: dialogId,
  role: 'dialog',
  'aria-modal': true,
  'aria-labelledby': titleId,
  'aria-describedby': descriptionId,
  onKeydown: handleKeyPress
}))

const titleProps = computed(() => ({
  id: titleId
}))

const descriptionProps = computed(() => ({
  id: descriptionId
}))
</script>

<template>
  <slot
    v-if="open"
    :open="open"
    :on-close="onClose"
    :dialog-props="dialogProps"
    :title-props="titleProps"
    :description-props="descriptionProps"
  >
    <div v-if="open" role="dialog" aria-modal="true">
      <template v-if="props.hasOwnBackdrop">
        <slot name="backdrop">
          <div class="fixed inset-0"></div>
        </slot>
      </template>
      <div ref="panelRef">
        <slot />
      </div>
    </div>
  </slot>
</template>
